import { WORLD_EXTENT_M } from "./constants.js";
const EPS = 1e-6;
/** The four corners of a yawed rectangle. */
export function rectCorners(r) {
    const c = Math.cos(r.yawRad);
    const s = Math.sin(r.yawRad);
    const hw = r.width / 2;
    const hd = r.depth / 2;
    const local = [
        [-hw, -hd],
        [hw, -hd],
        [hw, hd],
        [-hw, hd],
    ];
    return local.map(([lx, lz]) => [r.x + lx * c + lz * s, r.z - lx * s + lz * c]);
}
/** Separating-axis test; rectangles that merely touch do not overlap. */
function overlaps(a, b) {
    for (const poly of [a, b]) {
        for (let i = 0; i < 2; i++) {
            const p0 = poly[i];
            const p1 = poly[i + 1];
            const axis = [p1[1] - p0[1], -(p1[0] - p0[0])];
            const project = (pts) => {
                let min = Infinity;
                let max = -Infinity;
                for (const p of pts) {
                    const d = p[0] * axis[0] + p[1] * axis[1];
                    min = Math.min(min, d);
                    max = Math.max(max, d);
                }
                return [min, max];
            };
            const [aMin, aMax] = project(a);
            const [bMin, bMax] = project(b);
            const len = Math.hypot(axis[0], axis[1]);
            if (aMax <= bMin + EPS * len || bMax <= aMin + EPS * len)
                return false;
        }
    }
    return true;
}
const insideBounds = (pts, b) => pts.every(([x, z]) => x >= b.minX - EPS && x <= b.maxX + EPS && z >= b.minZ - EPS && z <= b.maxZ + EPS);
/** True when the point lies in `bounds` (edges included). */
export const pointInBounds = (x, z, b) => x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ;
/**
 * The single placement rule of the world (client ghost, server `WorldView`, tests): the footprint
 * must lie inside the world extent and inside its parcel (when one is named), and must not
 * overlap another building footprint (buildings) or parcel (parcels). Reasons are checked in
 * that order.
 */
export function checkPlacement(candidate, world) {
    const finite = [candidate.x, candidate.z, candidate.yawRad, candidate.width, candidate.depth];
    if (!finite.every(Number.isFinite) || candidate.width <= 0 || candidate.depth <= 0) {
        return { ok: false, reason: "outside_world" };
    }
    const corners = rectCorners(candidate);
    const extent = world.extent ?? WORLD_EXTENT_M;
    const limit = { minX: -extent, minZ: -extent, maxX: extent, maxZ: extent };
    if (!insideBounds(corners, limit))
        return { ok: false, reason: "outside_world" };
    if (candidate.parcelId && candidate.kind !== "parcel") {
        const parcel = world.parcels.find((p) => p.id === candidate.parcelId);
        if (!parcel || !insideBounds(corners, parcel.bounds)) {
            return { ok: false, reason: "outside_parcel" };
        }
    }
    if (candidate.kind === "parcel") {
        for (const p of world.parcels) {
            if (p.id === candidate.selfId)
                continue;
            const other = rectCorners({
                x: (p.bounds.minX + p.bounds.maxX) / 2,
                z: (p.bounds.minZ + p.bounds.maxZ) / 2,
                yawRad: 0,
                width: p.bounds.maxX - p.bounds.minX,
                depth: p.bounds.maxZ - p.bounds.minZ,
            });
            if (overlaps(corners, other))
                return { ok: false, reason: "overlap", withId: p.id };
        }
        return { ok: true };
    }
    if (candidate.kind === "building") {
        for (const f of world.footprints) {
            if (f.id === candidate.selfId || f.kind !== "building")
                continue;
            if (overlaps(corners, rectCorners(f)))
                return { ok: false, reason: "overlap", withId: f.id };
        }
    }
    return { ok: true };
}
/** The bounds of a `width` x `depth` parcel centered at `(cx, cz)`. */
export function parcelAt(cx, cz, width, depth) {
    return { minX: cx - width / 2, maxX: cx + width / 2, minZ: cz - depth / 2, maxZ: cz + depth / 2 };
}
//# sourceMappingURL=placement.js.map